import {
  loginRespostaSchema,
  PERMISSAO_IRRESTRITA,
  permissoesDosPapeis,
  temPermissao,
  type LoginResposta,
} from '@construtora/contracts';
import { useSyncExternalStore } from 'react';
import { definirAccessToken, requisitar } from './api.ts';

/**
 * Sessão do usuário.
 *
 * Fica num store externo, e não em contexto do React, porque a guarda de rota
 * do TanStack Router precisa ler o estado de forma **síncrona**, antes de
 * qualquer componente renderizar. Com contexto, a guarda dependeria de a
 * árvore já estar montada, e a rota protegida piscaria antes do redirecionamento.
 *
 * Nada aqui vai para `localStorage`: o access token vive em memória e o refresh
 * fica no cookie HttpOnly (.spec/005, seção 2).
 */

export type Sessao = LoginResposta['usuario'];

let sessao: Sessao | null = null;
const ouvintes = new Set<() => void>();

function notificar(): void {
  for (const ouvinte of ouvintes) ouvinte();
}

export function obterSessao(): Sessao | null {
  return sessao;
}

export function definirSessao(nova: Sessao | null): void {
  if (sessao === nova) return;

  sessao = nova;
  notificar();
}

function inscrever(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

export function useSessao(): Sessao | null {
  return useSyncExternalStore(inscrever, obterSessao, obterSessao);
}

/**
 * Permissões efetivas.
 *
 * Derivadas dos papéis com a mesma função que a API usa (`packages/contracts`).
 * A tela esconde o que o usuário não pode fazer; quem autoriza de verdade é o
 * backend — esconder botão não é controle de acesso (.spec/005, seção 3).
 */
let permissoesCache: { papeis: readonly string[]; lista: string[] } | null = null;

export function permissoesAtuais(): readonly string[] {
  const atual = obterSessao();
  if (!atual) return [];

  // A lista de papéis é a mesma referência enquanto a sessão não muda, então a
  // comparação por identidade basta e evita reexpandir a cada checagem.
  if (permissoesCache?.papeis === atual.papeis) return permissoesCache.lista;

  const lista = permissoesDosPapeis(atual.papeis);
  permissoesCache = { papeis: atual.papeis, lista };
  return lista;
}

export function podeFazer(permissao: string): boolean {
  // `autenticado` é o literal da API para "exige sessão, não exige papel". Na
  // tela vale o mesmo: é a permissão de "Minha conta", que todo usuário vê.
  if (permissao === 'autenticado') return obterSessao() !== null;
  return temPermissao(permissoesAtuais(), permissao);
}

export function usePode(permissao: string): boolean {
  // Depende da sessão: reassinar aqui garante nova renderização quando ela muda.
  useSessao();
  return podeFazer(permissao);
}

/**
 * O usuário não está vinculado a nenhuma empresa do grupo.
 *
 * Quem está nesse estado enxerga os seletores de **empresa, obra, depósito e
 * insumo vazios** — o recorte da API é por `usuarios_empresas`, e sem vínculo
 * não há o que listar. É configuração faltando, não cadastro vazio, e os dois
 * se parecem exatamente igual na tela: por isso existe o aviso.
 *
 * Quem tem a permissão irrestrita não é recortado, então para ele a lista vazia
 * significaria mesmo cadastro vazio — e um aviso ali mentiria.
 */
export function useSemEmpresaVinculada(): boolean {
  const atual = useSessao();

  if (!atual) return false;
  if (podeFazer(PERMISSAO_IRRESTRITA)) return false;

  return atual.empresas.length === 0;
}

interface RespostaSessao {
  accessToken: string;
  usuario: Sessao;
}

/**
 * Restaura a sessão a partir do cookie de refresh.
 *
 * Roda **uma vez** na carga da aplicação, não a cada montagem: em StrictMode o
 * efeito de montagem executa duas vezes, e duas rotações simultâneas do mesmo
 * refresh token são interpretadas pelo backend como reuso — a sessão legítima
 * seria encerrada por segurança logo ao abrir a página.
 */
export async function restaurarSessao(): Promise<void> {
  try {
    const dados = await requisitar<RespostaSessao>('/api/v1/auth/refresh', { method: 'POST' });
    definirAccessToken(dados.accessToken);
    definirSessao(dados.usuario);
  } catch {
    // Sem cookie ou cookie expirado: é o caminho normal de quem ainda não entrou.
    definirAccessToken(null);
    definirSessao(null);
  }
}

export function encerrarSessaoLocal(): void {
  definirAccessToken(null);
  definirSessao(null);
}

const usuarioSchema = loginRespostaSchema.shape.usuario;

/**
 * De quem é o token que acabou de ser renovado?
 *
 * Chamada pelo cliente HTTP a cada renovação, **antes** de o token entrar em
 * memória. O motivo de ela existir está em `api.ts`, em `renovarAccessToken`: o
 * refresh vem de um cookie que é do navegador inteiro, e a aba pode receber um
 * token perfeitamente válido de outra conta.
 *
 * Devolve `mesma` em toda dúvida, e são duas:
 *
 * - **Sem sessão em memória.** Não há o que contradizer. É o caso da carga da
 *   página, que restaura a sessão a partir do cookie — ali quem manda é o
 *   cookie mesmo, e o cabeçalho nasce junto com o token.
 * - **Corpo fora do contrato.** Uma resposta que o schema não reconhece pode ser
 *   uma mudança no servidor, e não uma troca de conta. Derrubar por isso
 *   transformaria um deploy da API em logout de todo mundo.
 *
 * Quando é a mesma pessoa, aproveita para pôr a sessão em dia. Papéis e vínculos
 * de empresa mudam enquanto a aba fica aberta — o administrador tira o papel de
 * Compras de alguém no meio da tarde —, e a renovação é a única hora em que o
 * servidor os manda de novo sem recarregar a página. Sem esta linha, o menu
 * continuaria oferecendo por 15 minutos telas que a API já recusa.
 */
export function conferirIdentidadeRenovada(identidade: unknown): 'mesma' | 'outra' {
  const analise = usuarioSchema.safeParse(identidade);
  if (!analise.success) return 'mesma';

  const atual = obterSessao();
  if (!atual) return 'mesma';

  // O `id` basta: usuário pertence a uma conta só, então id diferente é conta
  // diferente. O `tenantId` não vem nesta resposta, e o `tid` só existe dentro
  // do JWT — decodificá-lo no cliente para chegar à mesma conclusão seria
  // trabalho a mais pela mesma resposta.
  if (atual.id !== analise.data.id) return 'outra';

  definirSessao(analise.data);
  return 'mesma';
}
